import { useQuery } from '@tanstack/react-query'
import { api } from '../../api'

export type InsightCategory =
  | 'cycle' | 'sleep' | 'stress' | 'diet' | 'meds' | 'biometrics' | 'symptom' | 'trend'
export type InsightSeverity = 'good' | 'info' | 'watch' | 'alert'
export type InsightConfidence = 'strong' | 'moderate' | 'emerging'

export interface InsightEvidence {
  stat: string
  detail: string
  n: number
}

export type InsightViz =
  | { kind: 'bars'; data: { label: string; value: number }[]; unit?: string }
  | { kind: 'sparkline'; data: number[] }
  | { kind: 'corr'; r: number }
  | { kind: 'delta'; with: number | null; without: number | null; unit?: string }

export interface InsightAction {
  label: string
  kind: 'coach' | 'provider' | 'log'
  seed?: string | null
}

/**
 * An enriched "Your patterns" insight. Older callers only used
 * type/severity/title/body; the remaining fields are populated by the
 * candidate builder + LLM polish layer and are optional for safety.
 */
export interface PainPattern {
  id?: string
  type: string
  category?: InsightCategory
  severity: InsightSeverity
  confidence?: InsightConfidence
  title: string
  body: string
  evidence?: InsightEvidence
  viz?: InsightViz | null
  action?: InsightAction | null
}

export interface PatternMetrics {
  avg_pain_7d: number | null
  avg_pain_delta: number | null
  pain_free_days_7d: number
  top_symptom_30d: string | null
  top_symptom_key: string | null
  top_symptom_count: number
  next_high_pain_window: string | null
  next_high_pain_source: string | null
}

export interface SymptomFrequency {
  items: { symptom: string; label: string; count: number }[]
  days_with_data: number
}

export interface SleepPainBucket {
  bucket: string
  avg_pain: number | null
  days: number
}

export interface SleepPain {
  buckets: SleepPainBucket[]
  total_pairs: number
}

export interface CyclePhase {
  phase: string
  avg_pain: number | null
  days: number
  color: string
}

export interface CyclePhasePain {
  has_data: boolean
  data_quality?: 'full' | 'partial' | 'insufficient'
  complete_cycles?: number
  phases: CyclePhase[]
  message?: string | null
}

export interface WeeklyAverage {
  week_start: string
  avg: number
  days_logged: number
}

export interface PainTrendRegression {
  slope: number
  r_squared: number
  direction: 'improving' | 'worsening' | 'stable'
  pct_change: number
}

export interface PainTrend {
  weekly_averages: WeeklyAverage[]
  regression: PainTrendRegression | null
}

export interface TriggerItem {
  variable: string
  label: string
  correlation: number
  n: number
  direction: 'better' | 'worse'
  lag: string
}

export interface Triggers {
  better: TriggerItem[]
  worse: TriggerItem[]
  total_data_points: number
}

export interface TomorrowAlert {
  predicted_score: number
  risk_factors: string[]
  prn_medications: { name: string; dosage: string }[]
  show: boolean
}

export interface PainPatternsResponse {
  data_days: number
  metrics: PatternMetrics
  symptom_frequency: SymptomFrequency
  sleep_pain: SleepPain
  tomorrow_alert: TomorrowAlert | null
  cycle_phase_pain: CyclePhasePain
  pain_trend: PainTrend
  triggers: Triggers
  biometric_triggers: TriggerItem[]
  med_effectiveness: unknown
  patterns_detected: PainPattern[]
  data_engagement: unknown
}

export function usePainPatterns(enabled = true) {
  return useQuery({
    queryKey: ['patient', 'pain-patterns'],
    queryFn: () => api.get<PainPatternsResponse>('/api/patient/pain-patterns'),
    staleTime: 5 * 60_000,
    enabled,
  })
}
